/* eslint i18n-text/no-en: off */
import type AutocompleteElement from '@github/auto-complete-element'
import type DetailsDialogElement from '@github/details-dialog-element'
import {on} from 'delegated-events'
import {isEnterprise} from '@github-ui/runtime-environment'

function orgTwoFactorInput(): HTMLInputElement {
  return document.querySelector<HTMLInputElement>('.js-org-enable-two-factor')!
}

function twoFactorSecureMethodsPresent(): boolean {
  return document.querySelector<HTMLInputElement>('.js-org-two-factor-secure-methods-currently-enabled') != null
}

function enablingTwoFactor(): boolean {
  const twoFactorCurrentlyEnabled =
    document.querySelector<HTMLInputElement>('.js-org-two-factor-currently-enabled')!.value === '0'
  return orgTwoFactorInput().checked && twoFactorCurrentlyEnabled
}

function twoFactorRequirementSettingChanged(): boolean {
  const enabled = document.querySelector<HTMLInputElement>('.js-org-two-factor-currently-enabled')!.value === '1'
  const enabling = orgTwoFactorInput().checked
  return enabled !== enabling
}

function twoFactorRequirementSecureMethodSettingChanged(): boolean {
  const current = document.querySelector<HTMLInputElement>('.js-org-two-factor-secure-methods-currently-enabled')
  const checkbox = document.querySelector<HTMLInputElement>('.js-org-enable-two-factor-secure-methods')
  if (current == null || checkbox == null) {
    return false
  }

  const enabled = current.value === '1'
  const enabling = checkbox.checked

  return enabled !== enabling
}

// disable the 2FA requirement checkbox if the user has enabled the secure methods only checkbox
// don't use the .disabled property as that prevents the value from being submitted with the form
function setTwoFactorRequirementCheckboxState(requirementCheckbox: HTMLInputElement, isDisabled: boolean): void {
  if (isDisabled) {
    requirementCheckbox.readOnly = true
    requirementCheckbox.style.pointerEvents = 'none'
    requirementCheckbox.style.opacity = '0.5'
  } else {
    requirementCheckbox.readOnly = false
    requirementCheckbox.style.pointerEvents = 'auto'
    requirementCheckbox.style.opacity = '1'
  }
}

// update the 'submit' button state based on whether either of the two-factorsettings have changed
function updateTwoFactorRequirementSubmitButtonState(hasChanges: boolean) {
  // there are two possible submit buttons that get rendered
  // the submitFormButton is an invalid button that gets rendered when a user has clicked multiple times on the web page
  const submitButton = document.querySelector<HTMLButtonElement>('.js-org-two-factor-submit-button')

  if (submitButton == null) {
    return
  }
  if (hasChanges) {
    submitButton.style.pointerEvents = 'auto'
    submitButton.style.opacity = '1'
  } else {
    submitButton.style.pointerEvents = 'none'
    submitButton.style.opacity = '0.5'
  }
  submitButton.setAttribute('aria-disabled', (!hasChanges).toString())
}

function hideAllDialogContent() {
  for (const span of document.querySelectorAll<HTMLElement>('.js-org-two-factor-confirmation-dialog-body span')) {
    span.hidden = true
  }
}

function setDialogState(
  dialogTitle: HTMLElement,
  dialogButton: HTMLElement,
  titleText: string,
  elementId: string,
  isDanger: boolean,
) {
  dialogTitle.textContent = titleText
  document.querySelector<HTMLElement>(elementId)!.hidden = false
  dialogButton.classList.remove(isDanger ? 'Button--primary' : 'Button--danger')
  dialogButton.classList.add(isDanger ? 'Button--danger' : 'Button--primary')
}

function updateTwoFactorConfirmationDialogForEnterprise() {
  const requirementChanged = twoFactorRequirementSettingChanged()
  if (!requirementChanged) {
    return
  }

  const dialogTitle = document.querySelector<HTMLElement>('.js-org-two-factor-confirmation-dialog .Box-title')!
  const dialogButton = document.querySelector<HTMLElement>('.js-org-two-factor-confirmation-dialog-button')!
  const orgName = document.querySelector<HTMLInputElement>('#two-factor-org-name')!.value
  const requirementEnabled =
    document.querySelector<HTMLInputElement>('.js-org-two-factor-currently-enabled')!.value === '1'

  const disablingRequirement = requirementEnabled && requirementChanged
  const enablingRequirement = !requirementEnabled && requirementChanged

  hideAllDialogContent()

  if (disablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to remove the two-factor authentication requirement for the ${orgName} organization?`,
      '#tfrc-disabling-requirement',
      true,
    )
  } else if (enablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to require two-factor authentication for all users in the ${orgName} organization?`,
      '#tfrc-enabling-requirement-without-secure-methods',
      false,
    )
  }
}

function updateTwoFactorConfirmationDialogForDotcom() {
  if (!twoFactorSecureMethodsPresent()) {
    return
  }

  const requirementChanged = twoFactorRequirementSettingChanged()
  const secureMethodsChanged = twoFactorRequirementSecureMethodSettingChanged()
  if (!requirementChanged && !secureMethodsChanged) {
    return
  }

  const requirementEnabled =
    document.querySelector<HTMLInputElement>('.js-org-two-factor-currently-enabled')!.value === '1'
  const secureMethodsEnabled =
    document.querySelector<HTMLInputElement>('.js-org-two-factor-secure-methods-currently-enabled')!.value === '1'

  const disablingRequirement = requirementEnabled && requirementChanged
  const enablingRequirement = !requirementEnabled && requirementChanged
  const disablingSecureMethods = secureMethodsEnabled && secureMethodsChanged
  const enablingSecureMethods = !secureMethodsEnabled && secureMethodsChanged

  const dialogTitle = document.querySelector<HTMLElement>('.js-org-two-factor-confirmation-dialog .Box-title')!
  const dialogButton = document.querySelector<HTMLElement>('.js-org-two-factor-confirmation-dialog-button')!
  const orgName = document.querySelector<HTMLInputElement>('#two-factor-org-name')!.value

  hideAllDialogContent()

  if (disablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to remove the two-factor authentication requirement for the ${orgName} organization?`,
      '#tfrc-disabling-requirement',
      true,
    )
  } else if (enablingRequirement && enablingSecureMethods) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to require two-factor authentication and only allow secure methods for all users in the ${orgName} organization?`,
      '#tfrc-enabling-requirement-with-secure-methods',
      false,
    )
  } else if (enablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to require two-factor authentication for all users in the ${orgName} organization?`,
      '#tfrc-enabling-requirement-without-secure-methods',
      false,
    )
  } else if (disablingSecureMethods) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to allow all two-factor authentication methods for the ${orgName} organization?`,
      '#tfrc-disabling-secure-methods-only',
      true,
    )
  } else {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to only allow secure methods of two-factor authentication for all users in the ${orgName} organization?`,
      '#tfrc-enabling-secure-methods-only',
      false,
    )
  }
}

// Updates the two-factor requirement settings dialog. Because the dialog content is dependent on the state of the
// two nested checkboxes, we are unable to predict the dialog content on page load; rather we have to do it here
// whenever the state of either checkbox changes.
function updateTwoFactorConfirmationDialog() {
  if (isEnterprise()) {
    updateTwoFactorConfirmationDialogForEnterprise()
  } else {
    updateTwoFactorConfirmationDialogForDotcom()
  }
}

function shouldPreventDialogForEnterprise(): boolean {
  return !twoFactorRequirementSettingChanged()
}

function shouldPreventDialogForDotcom(): boolean {
  if (!twoFactorSecureMethodsPresent()) {
    return !enablingTwoFactor()
  }
  return !twoFactorRequirementSettingChanged() && !twoFactorRequirementSecureMethodSettingChanged()
}

// bypass showing the two-factor confirmation dialog if we're not enabling two-factor. always show the confirmation dialog
// if the new two-factor secure methods setting is not present.
on('click', '.js-org-two-factor-submit-button', function (event) {
  updateTwoFactorConfirmationDialog()

  const shouldPreventDialog = isEnterprise() ? shouldPreventDialogForEnterprise() : shouldPreventDialogForDotcom()

  if (shouldPreventDialog) {
    event.preventDefault()
  }
})

function handleTwoFactorChangeForEnterprise(requirementCheckbox: HTMLInputElement | null) {
  if (requirementCheckbox == null) {
    return
  }
  const hasChanges = twoFactorRequirementSettingChanged()
  updateTwoFactorRequirementSubmitButtonState(hasChanges)
}

function handleTwoFactorChangeForDotcom(
  requirementCheckbox: HTMLInputElement | null,
  secureMethodsRow: HTMLElement | null,
  secureMethodsCheckbox: HTMLInputElement | null,
  warningBanner: HTMLElement | null,
) {
  if (requirementCheckbox == null || secureMethodsRow == null || warningBanner == null) {
    return
  }

  if (requirementCheckbox.checked && secureMethodsCheckbox && !secureMethodsCheckbox.checked) {
    secureMethodsCheckbox.checked = true
  }

  setTwoFactorRequirementCheckboxState(requirementCheckbox, secureMethodsCheckbox?.checked ?? false)

  const hasChanges = twoFactorRequirementSettingChanged() || twoFactorRequirementSecureMethodSettingChanged()
  if (hasChanges && warningBanner) {
    warningBanner.hidden = !requirementCheckbox?.checked && !secureMethodsCheckbox?.checked
  }

  updateTwoFactorRequirementSubmitButtonState(hasChanges)
}

on('change', '.js-org-enable-two-factor', function () {
  const requirementCheckbox = document.querySelector<HTMLInputElement>('.js-org-enable-two-factor')
  const secureMethodsRow = document.querySelector<HTMLElement>('.js-two-factor-secure-methods')
  const secureMethodsCheckbox = document.querySelector<HTMLInputElement>('.js-org-enable-two-factor-secure-methods')
  const warningBanner = document.querySelector<HTMLElement>('.js-org-two-factor-requirement-warning-banner')

  if (isEnterprise()) {
    handleTwoFactorChangeForEnterprise(requirementCheckbox)
  } else {
    handleTwoFactorChangeForDotcom(requirementCheckbox, secureMethodsRow, secureMethodsCheckbox, warningBanner)
  }
})

on('change', '.js-org-enable-two-factor-secure-methods', function () {
  const requirementCheckbox = document.querySelector<HTMLInputElement>('.js-org-enable-two-factor')
  const secureMethodsCheckbox = document.querySelector<HTMLInputElement>('.js-org-enable-two-factor-secure-methods')
  const warningBanner = document.querySelector<HTMLElement>('.js-org-two-factor-requirement-warning-banner')

  if (requirementCheckbox == null || secureMethodsCheckbox == null || warningBanner == null) {
    // need to tolerate these being missing from the DOM if the feature hasn't been fully rolled out
    return
  }
  const hasChanges = twoFactorRequirementSettingChanged() || twoFactorRequirementSecureMethodSettingChanged()

  // automatically select the 2FA requirement checkbox if the user is enabling the secure methods only checkbox
  if (secureMethodsCheckbox?.checked && requirementCheckbox && !requirementCheckbox.checked) {
    requirementCheckbox.checked = true
  }

  setTwoFactorRequirementCheckboxState(requirementCheckbox, secureMethodsCheckbox?.checked ?? false)

  // show warning banner if there's any new changes
  if (hasChanges && warningBanner) {
    warningBanner.hidden = !requirementCheckbox?.checked && !secureMethodsCheckbox?.checked
  }

  updateTwoFactorRequirementSubmitButtonState(hasChanges)
})
